import { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { SET_CURRENT_VEHICLE_FAVORITED } from '@/vdp/VehicleDetailsContainer/actionTypes';
import ModalContext from '~/context/vdp/ModalContext';
import {
  type FavoritesArguments,
  type LifeCycleFn,
  useAddFavorite as useAddFavoriteCommon
} from '~/hooks/common/favorites';
import { getIsReservableVehicle, getVehicleId } from '~/selectors';
import useSelector from '~/store/useSelector';

export function useBeforeAddFavorite() {
  const dispatch = useDispatch();
  const currentVehicleId = useSelector(getVehicleId);

  return useCallback(
    ({ vehicleId }: FavoritesArguments) => {
      if (currentVehicleId === vehicleId) {
        dispatch({
          type: SET_CURRENT_VEHICLE_FAVORITED,
          payload: { isFavorited: true }
        });
      }
    },
    [currentVehicleId, dispatch]
  );
}

export function useMaybeOpenReservableNotificationModal() {
  const {
    isReservableNotificationModalOpen,
    setReservableNotificationModalOpen
  } = useContext(ModalContext);
  const currentVehicleId = useSelector(getVehicleId);
  const isReservable = useSelector(getIsReservableVehicle);

  return useCallback(
    (args: FavoritesArguments) => {
      if (
        args.vehicleId === currentVehicleId &&
        isReservable &&
        !isReservableNotificationModalOpen
      ) {
        setReservableNotificationModalOpen(true);
      }
    },
    [
      currentVehicleId,
      isReservable,
      isReservableNotificationModalOpen,
      setReservableNotificationModalOpen
    ]
  );
}

type Options = {
  onAddFavorite?: LifeCycleFn;
};

export default function useAddFavorite(
  opts: Options = {}
): (args: FavoritesArguments) => Promise<void> {
  const maybeOpenReservableNotificationModal =
    useMaybeOpenReservableNotificationModal();

  const addFavorite = useAddFavoriteCommon({
    beforeAddFavorite: useBeforeAddFavorite(),
    onAddFavorite: opts.onAddFavorite ?? maybeOpenReservableNotificationModal
  });

  return useCallback(
    (args: FavoritesArguments) =>
      addFavorite({ nativeEventSource: 'vdp', ...args }),
    [addFavorite]
  );
}
